/**
 * Interactive dependency DAG, rendered as hand-rolled SVG.
 *
 * Nodes are tickets, edges are `blocks` (solid, source above target) and
 * `parent` (dashed, epic above child). Colour encodes ticket status. Pan by
 * dragging, zoom with the wheel or the +/- controls, click a node to open its
 * ticket, hover to spotlight its immediate neighbourhood.
 *
 * Above `treeThreshold` nodes the layered drawing stops being readable, so the
 * component degrades to a grouped indented tree instead of a hairball. The
 * viewer can force either mode.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { PriorityTag, StatusChip } from '../../views/city/parts.jsx'
import { STATUSES, STATUS_META, cls } from '../../views/city/shared.js'
import { NODE_H, NODE_W, connectedComponents, forestRows, layoutGraph } from './layout.js'
import './graph.css'

const MIN_SCALE = 0.15
const MAX_SCALE = 2.5

/** Shorten a title to fit the fixed-width node box. */
function clip(text, max) {
  if (!text) return ''
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`
}

/** Smooth-ish polyline: straight segments with rounded vertical entry. */
function edgePath(points) {
  if (points.length === 2) {
    const [a, b] = points
    const midY = (a.y + b.y) / 2
    return `M ${a.x} ${a.y} C ${a.x} ${midY}, ${b.x} ${midY}, ${b.x} ${b.y}`
  }
  let d = `M ${points[0].x} ${points[0].y}`
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1]
    const cur = points[i]
    const midY = (prev.y + cur.y) / 2
    d += ` C ${prev.x} ${midY}, ${cur.x} ${midY}, ${cur.x} ${cur.y}`
  }
  return d
}

/**
 * @param {object} props
 * @param {Array} props.nodes           GraphNode[] from `GET /api/v1/graph`.
 * @param {Array} props.edges           GraphEdge[] from the same call.
 * @param {number} [props.height]       Viewport height in px.
 * @param {string} [props.focusKey]     Ticket key to highlight on mount.
 * @param {number} [props.treeThreshold] Node count above which we degrade.
 *   Raised from 120 to 400 on 2026-08-20: with component packing and row
 *   wrapping in `layout.js`, 203 nodes now draw at a ~1.6 aspect ratio and
 *   "Fit" scales to ~0.25, so the SVG is legible and is the default view
 *   again. Before that the whole seeded graph silently fell back to the tree.
 * @param {string} [props.mode]         'auto' | 'graph' | 'tree'.
 * @param {Function} [props.onModeChange] Called with the resolved mode.
 */
export default function DependencyGraph({
  nodes,
  edges,
  height = 560,
  focusKey = null,
  treeThreshold = 400,
  mode = 'auto',
  onModeChange,
}) {
  const navigate = useNavigate()
  const wrapRef = useRef(null)
  const [view, setView] = useState({ x: 0, y: 0, k: 1 })
  const [size, setSize] = useState({ w: 900, h: height })
  const [hover, setHover] = useState(null)
  const [panning, setPanning] = useState(false)
  const drag = useRef(null)
  const movedRef = useRef(false)

  const laid = useMemo(() => layoutGraph(nodes || [], edges || []), [nodes, edges])

  const resolvedMode =
    mode === 'graph' || mode === 'tree'
      ? mode
      : (nodes || []).length > treeThreshold || laid.widest > 60
        ? 'tree'
        : 'graph'

  useEffect(() => {
    if (onModeChange) onModeChange(resolvedMode)
  }, [resolvedMode, onModeChange])

  /** Neighbour index, for hover spotlighting. */
  const neighbours = useMemo(() => {
    const map = new Map()
    for (const n of laid.nodes) map.set(n.id, new Set([n.id]))
    for (const e of laid.edges) {
      if (map.has(e.from)) map.get(e.from).add(e.to)
      if (map.has(e.to)) map.get(e.to).add(e.from)
    }
    return map
  }, [laid])

  /** Fit the whole diagram into the viewport. */
  const fit = useCallback(() => {
    const el = wrapRef.current
    if (!el) return
    const w = el.clientWidth || 900
    const h = height
    const pad = 32
    const k = Math.max(
      MIN_SCALE,
      Math.min(1.15, Math.min((w - pad * 2) / (laid.width || 1), (h - pad * 2) / (laid.height || 1))),
    )
    setSize({ w, h })
    setView({
      x: (w - laid.width * k) / 2,
      y: pad,
      k,
    })
  }, [laid.width, laid.height, height])

  useEffect(() => {
    fit()
  }, [fit])

  useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(() => setSize((s) => ({ ...s, w: el.clientWidth || s.w })))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Centre on the focused ticket once the layout is known.
  useEffect(() => {
    if (!focusKey || resolvedMode !== 'graph') return
    const target = laid.nodes.find((n) => n.key === focusKey)
    if (!target) return
    setView((v) => ({
      ...v,
      x: size.w / 2 - (target.x + NODE_W / 2) * v.k,
      y: size.h / 2 - (target.y + NODE_H / 2) * v.k,
    }))
  }, [focusKey, laid, resolvedMode, size.w, size.h])

  const onWheel = (event) => {
    event.preventDefault()
    const rect = wrapRef.current.getBoundingClientRect()
    const px = event.clientX - rect.left
    const py = event.clientY - rect.top
    setView((v) => {
      const factor = Math.exp(-event.deltaY * 0.0015)
      const k = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.k * factor))
      const ratio = k / v.k
      return { k, x: px - (px - v.x) * ratio, y: py - (py - v.y) * ratio }
    })
  }

  const onPointerDown = (event) => {
    if (event.button !== 0) return
    movedRef.current = false
    drag.current = { x: event.clientX, y: event.clientY, vx: view.x, vy: view.y, moved: false }
    setPanning(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event) => {
    const d = drag.current
    if (!d) return
    const dx = event.clientX - d.x
    const dy = event.clientY - d.y
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      d.moved = true
      movedRef.current = true
    }
    setView((v) => ({ ...v, x: d.vx + dx, y: d.vy + dy }))
  }

  const endPan = (event) => {
    if (drag.current && event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    drag.current = null
    setPanning(false)
  }

  const zoomBy = (factor) =>
    setView((v) => {
      const k = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.k * factor))
      const ratio = k / v.k
      return { k, x: size.w / 2 - (size.w / 2 - v.x) * ratio, y: height / 2 - (height / 2 - v.y) * ratio }
    })

  if (!nodes || !nodes.length) {
    return (
      <div className="pm-graph" style={{ height }}>
        <div className="pm-empty" style={{ paddingTop: height / 2 - 20 }}>
          No `blocks` or `parent` links in scope — nothing to draw.
        </div>
      </div>
    )
  }

  if (resolvedMode === 'tree') {
    return <GroupedTree nodes={nodes} edges={edges} height={height} focusKey={focusKey} />
  }

  const hotSet = hover ? neighbours.get(hover) : null

  return (
    <div className={cls('pm-graph', panning && 'is-panning')} style={{ height }} ref={wrapRef}>
      <div className="pm-graph-controls">
        <button type="button" className="btn" onClick={() => zoomBy(1.25)} title="Zoom in">
          +
        </button>
        <button type="button" className="btn" onClick={() => zoomBy(0.8)} title="Zoom out">
          −
        </button>
        <button type="button" className="btn" onClick={fit} title="Fit to view">
          Fit
        </button>
      </div>

      <svg
        width="100%"
        height={height}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPan}
        onPointerCancel={endPan}
        onPointerLeave={endPan}
        role="img"
        aria-label={`Dependency graph, ${laid.nodes.length} tickets, ${laid.edges.length} links`}
      >
        <defs>
          <marker
            id="pm-arrow"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--st-edge, #b3bac4)" />
          </marker>
          <marker
            id="pm-arrow-hot"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--accent, #6366f1)" />
          </marker>
        </defs>

        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
          {laid.edges.map((e) => {
            const hot = hotSet && (hotSet.has(e.from) || hotSet.has(e.to))
            return (
              <path
                key={`e${e.id}-${e.from}-${e.to}`}
                className={cls(
                  'pm-edge',
                  e.type === 'parent' && 'is-parent',
                  hotSet && !hot && 'is-dim',
                  hot && 'is-hot',
                )}
                d={edgePath(e.points)}
                markerEnd={`url(#${hot ? 'pm-arrow-hot' : 'pm-arrow'})`}
              >
                <title>
                  {e.type === 'parent' ? 'epic of' : 'blocks'}
                  {e.reversed ? ' (cycle — drawn reversed)' : ''}
                </title>
              </path>
            )
          })}

          {laid.nodes.map((n) => {
            const meta = STATUS_META[n.status] || { color: 'var(--text-muted)', label: n.status }
            const hot = !hotSet || hotSet.has(n.id)
            return (
              <g
                key={n.id}
                className={cls('pm-node', !hot && 'is-dim', focusKey === n.key && 'is-focus')}
                transform={`translate(${n.x} ${n.y})`}
                role="link"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHover(n.id)}
                onMouseLeave={() => setHover(null)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') navigate(`/t/${n.key}`)
                }}
                onClick={() => {
                  if (!movedRef.current) navigate(`/t/${n.key}`)
                }}
              >
                <rect
                  className="pm-node-body"
                  width={NODE_W}
                  height={NODE_H}
                  rx="6"
                  fill="var(--bg-raised, #ffffff)"
                  stroke="var(--border, #d8dce2)"
                />
                <rect width="4" height={NODE_H} rx="2" fill={meta.color} />
                <text className="pm-node-key" x="12" y="17">
                  {n.key}
                </text>
                <text
                  className="pm-node-key"
                  x={NODE_W - 10}
                  y="17"
                  textAnchor="end"
                  fill={meta.color}
                >
                  {n.priority.toUpperCase()}
                </text>
                <text className="pm-node-title" x="12" y="33">
                  {clip(n.title, 24)}
                </text>
                <title>{`${n.key} \u00b7 ${n.status} \u00b7 ${n.priority}\n${n.title}${
                  n.city_slugs && n.city_slugs.length ? `\ncities: ${n.city_slugs.join(', ')}` : ''
                }`}</title>
              </g>
            )
          })}
        </g>
      </svg>

      <div className="pm-graph-legend">
        {STATUSES.filter((s) => laid.nodes.some((n) => n.status === s)).map((s) => (
          <span className="chip" key={s}>
            <span className="pm-dot" style={{ background: STATUS_META[s].color }} />
            {STATUS_META[s].label}
          </span>
        ))}
        <span className="chip">{laid.nodes.length} tickets</span>
        <span className="chip">{laid.edges.length} links</span>
        {laid.reversed ? (
          <span className="chip" title="Cyclic dependencies were reversed to lay the graph out">
            {laid.reversed} cycle edge{laid.reversed === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
    </div>
  )
}

/**
 * The graceful degradation: one indented tree per weakly connected component,
 * biggest first. Always legible, however many nodes there are.
 */
function GroupedTree({ nodes, edges, height, focusKey }) {
  const groups = useMemo(() => connectedComponents(nodes, edges), [nodes, edges])
  return (
    <div className="pm-graph" style={{ height, overflow: 'auto' }}>
      <div className="pm-tree">
        <div className="pm-empty" style={{ textAlign: 'left' }}>
          {nodes.length} tickets in {groups.length} dependency cluster
          {groups.length === 1 ? '' : 's'} — too dense to draw legibly, showing grouped trees.
        </div>
        {groups.map((group, gi) => (
          <TreeGroup
            key={group[0].id}
            index={gi}
            group={group}
            edges={edges}
            focusKey={focusKey}
          />
        ))}
      </div>
    </div>
  )
}

/** One connected cluster, rendered as an indented forest. */
function TreeGroup({ group, edges, index, focusKey }) {
  const rows = useMemo(() => forestRows(group, edges), [group, edges])
  if (group.length === 1) {
    return (
      <TreeRow row={{ node: group[0], depth: 0, viaCycle: false }} focusKey={focusKey} />
    )
  }
  return (
    <div>
      <div className="pm-card-head" style={{ borderTop: '1px solid var(--border, #d8dce2)' }}>
        <span>
          Cluster {index + 1} · {group.length} tickets
        </span>
      </div>
      {rows.map((row, i) => (
        <TreeRow key={`${row.node.id}-${i}`} row={row} focusKey={focusKey} />
      ))}
    </div>
  )
}

/** One row of the degraded tree view. */
function TreeRow({ row, focusKey }) {
  const { node, depth, viaCycle } = row
  return (
    <Link
      className="pm-tree-row"
      to={`/t/${node.key}`}
      style={focusKey === node.key ? { outline: '1px solid var(--accent, #6366f1)' } : undefined}
    >
      <span className="pm-tree-guide">{depth ? `${'│  '.repeat(depth - 1)}└─ ` : ''}</span>
      <span className="key">{node.key}</span>
      <PriorityTag priority={node.priority} />
      <span className="pm-ticket-title">{node.title}</span>
      <StatusChip status={node.status} />
      {viaCycle ? <span className="chip">cycle</span> : null}
    </Link>
  )
}
